import React, { useState } from 'react';
import { Loader2, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { api, errMsg } from '../api';

const BIRTH = {
  match: ['Coincide', 'text-[#5FBF8E] border-[#5FBF8E]/40'],
  partial: ['Coincide (dato parziale)', 'text-[#D4AF37] border-[#D4AF37]/40'],
  mismatch: ['Non coincide', 'text-[#E37A72] border-[#E37A72]/40'],
};
export const fmtBirth = (d) => (d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d.split('-').reverse().join('/') : d);
const Alt = ({ c }) => (c?.birth_date_alt?.length ? <span className="text-[#5A5F67]"> (altra banca dati: {c.birth_date_alt.join(', ')})</span> : null);

export function BirthBadge({ m, testid }) {
  const [label, cls] = BIRTH[m] || ['Non confrontabile', 'text-[#5A5F67] border-[#1F242C]'];
  return <span className={`text-[10px] font-mono tracking-wider px-2 py-0.5 border rounded-sm ${cls}`} data-testid={testid}>{label}</span>;
}

export function BirthCompare({ s, idx }) {
  const m = s.matched;
  if (s.kind !== 'person' || (!s.birth_date && !m)) return null;
  return (
    <div className="grid sm:grid-cols-3 gap-3 border border-[#1F242C] bg-[#07080A] px-4 py-3 mb-3 text-xs" data-testid={`aml-birth-${idx}`}>
      <div><p className="text-[#5A5F67] uppercase tracking-[0.12em] text-[10px]">Data di nascita indicata</p><p className="text-[#E5E5E5] mt-1" data-testid={`aml-birth-${idx}-provided`}>{fmtBirth(s.birth_date) || '—'}</p></div>
      <div><p className="text-[#5A5F67] uppercase tracking-[0.12em] text-[10px]">Data di nascita in banca dati</p><p className="text-[#E5E5E5] mt-1" data-testid={`aml-birth-${idx}-found`}>{m?.birth_date || (m ? 'Non presente' : 'Nessun soggetto identificato')}<Alt c={m} /></p></div>
      <div><p className="text-[#5A5F67] uppercase tracking-[0.12em] text-[10px] mb-1">Corrispondenza</p><BirthBadge m={m?.birth_match} testid={`aml-birth-${idx}-match`} /></div>
    </div>
  );
}

function Candidate({ c, j, idx, provided, busy, onPick }) {
  return (
    <div className="border border-[#1F242C] bg-[#07080A] p-4" data-testid={`aml-cand-${idx}-${j}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-[#F2F2F2]">{c.name}</p>
          <p className="text-xs text-[#8A8F97] mt-0.5">{[c.entity_type, c.location, (c.nationalities || []).join(', '), (c.tags || []).join(', ')].filter(Boolean).join(' · ')}</p>
        </div>
        <button disabled={busy} onClick={() => onPick(c.id)} className="h-9 px-3 text-xs flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50" data-testid={`aml-pick-${idx}-${j}`}>
          <UserCheck size={14} />Prosegui con questo soggetto
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs">
        <span className="text-[#5A5F67]">Indicata: <span className="text-[#C9CCD1]">{fmtBirth(provided) || '—'}</span></span>
        <span className="text-[#5A5F67]">In banca dati: <span className="text-[#C9CCD1]" data-testid={`aml-cand-${idx}-${j}-birth`}>{c.birth_date || 'Non presente'}</span><Alt c={c} /></span>
        <BirthBadge m={c.birth_match} testid={`aml-cand-${idx}-${j}-match`} />
        <span className="text-[#5A5F67]">{c.evidences_count} riscontri</span>
      </div>
      {c.recap && <p className="text-xs text-[#8A8F97] mt-2 line-clamp-2">{c.recap}</p>}
    </div>
  );
}

export default function CandidatePicker({ s, idx, checkId, onUpdate, onCancel }) {
  const [busy, setBusy] = useState(false);
  const [confirmNone, setConfirmNone] = useState(false);
  const total = s.candidates.reduce((n, c) => n + (c.evidences_count || 0), 0);
  const pick = async (entityId) => {
    setBusy(true);
    try { onUpdate((await api.post(`/aml/checks/${checkId}/subjects/${idx}/select`, { entity_id: entityId })).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2" data-testid={`aml-picker-${idx}`}>
      <p className="text-sm text-[#D4AF37] mb-1">Sono stati identificati {s.candidates.length} soggetti diversi</p>
      <p className="text-xs text-[#8A8F97] mb-3">Confronta la data di nascita e scegli il soggetto su cui proseguire: il risultato terrà conto solo dei suoi riscontri.</p>
      <div className="grid gap-2">{s.candidates.map((c, j) => <Candidate key={c.id || j} c={c} j={j} idx={idx} provided={s.birth_date} busy={busy} onPick={pick} />)}</div>
      <div className="flex flex-wrap gap-2 mt-3">
        {!confirmNone ? (
          <button disabled={busy} onClick={() => setConfirmNone(true)} className="h-9 px-3 text-xs flex items-center gap-2 border border-[#1F242C] text-[#8A8F97] hover:text-[#E5E5E5] transition-colors disabled:opacity-50" data-testid={`aml-pick-${idx}-none`}>
            <UserX size={14} />Nessuno corrisponde
          </button>
        ) : (
          <div className="flex flex-wrap items-center gap-2 text-xs border border-[#E37A72]/40 px-3 py-2" data-testid={`aml-pick-${idx}-none-confirm-box`}>
            <span className="text-[#E37A72]">I {total} riscontri trovati verranno esclusi dal risultato. Confermi?</span>
            <button disabled={busy} onClick={() => pick(null)} className="h-8 px-3 border border-[#E37A72]/50 text-[#E37A72] hover:bg-[#E37A72]/10 flex items-center gap-2" data-testid={`aml-pick-${idx}-none-confirm`}>
              {busy && <Loader2 size={13} className="animate-spin" />}Sì, nessuno corrisponde
            </button>
            <button onClick={() => setConfirmNone(false)} className="h-8 px-2 text-[#5A5F67] hover:text-[#E5E5E5]" data-testid={`aml-pick-${idx}-none-back`}>No</button>
          </div>
        )}
        {onCancel && <button onClick={onCancel} className="h-9 px-3 text-xs text-[#5A5F67] hover:text-[#E5E5E5]" data-testid={`aml-pick-${idx}-cancel`}>Annulla</button>}
      </div>
    </div>
  );
}
